/**
 * Trigger builder — pure helpers (Data Design PRD §6.7, task #206).
 *
 * The "When…" card and the activation step both mount in the workflow editor,
 * but their two loadbearing decisions are PURE and live here so they are
 * unit-testable without a DOM or a React tree:
 *
 *   1. `groupSubscribableEvents` — how the honest event picker
 *      (`subscribableEventTypes()`) is grouped for the UI: by aggregate prefix
 *      (the segment before the first `:`). If the §5.4 semantic registry lane
 *      has landed, per-type descriptions slot in; if not, the group header is
 *      just the prefix and descriptions are empty — the graceful-degrade seam
 *      the PRD calls for.
 *
 *   2. `resolveTriggerActivation` — the FOUR-GATE derivation the activation
 *      step renders. A trigger is ARMED only when all four gates are green:
 *      (a) the workflow is ACTIVE, (b) the subscription is ENABLED, (c) the
 *      consumer opt-in `agents.triggers.autorun` is on, and (d) the drainer
 *      (autopilot tick — in-app scheduler or OS daemon) is live. Anything red
 *      is named with a plain-language reason and the setting/route that fixes
 *      it, so the user leaves the builder armed or told exactly why not.
 *
 * No imports, no DB — the UI resolves the raw state (via server actions over
 * the core seams) and hands it here; the test hands a fixture.
 */
/**
 * Group the honest event-type list by aggregate prefix. `descriptions` is an
 * optional (§5.4) map from full type → human description; absent entries
 * degrade to no description and the prefix as the header (the PRD's
 * "if the parallel lane hasn't landed" path). Types with no `:` are grouped
 * under themselves (defensive — every emitted type is `aggregate:verb`).
 */
export function groupSubscribableEvents(types, descriptions) {
    const byPrefix = new Map();
    for (const type of types) {
        const colon = type.indexOf(":");
        const prefix = colon > 0 ? type.slice(0, colon) : type;
        const label = colon > 0 ? type.slice(colon + 1) : type;
        const opt = {
            type,
            label,
            description: descriptions?.[type],
        };
        const list = byPrefix.get(prefix);
        if (list)
            list.push(opt);
        else
            byPrefix.set(prefix, [opt]);
    }
    return [...byPrefix.entries()]
        .sort(([a], [b]) => a.localeCompare(b))
        .map(([prefix, options]) => ({
        prefix,
        // Header degrades to the bare prefix until the semantic registry
        // provides a friendlier aggregate label (keyed by the aggregate itself).
        label: descriptions?.[prefix] ?? prefix,
        options: options.sort((a, b) => a.label.localeCompare(b.label)),
    }));
}
/**
 * Derive the four-gate activation state. The order is the causal chain a user
 * reads top-to-bottom: workflow must be active → its subscription enabled →
 * the consumer opt-in on → the drainer running. When there's no subscription
 * yet, every downstream gate is honestly reported as not-yet-satisfied (the
 * "When…" card hasn't been saved).
 */
export function resolveTriggerActivation(state) {
    const workflowActive = state.loopStatus === "active";
    const subEnabled = state.hasSubscription && state.subscriptionEnabled;
    const gates = [
        {
            id: "workflow",
            label: "Workflow active",
            ok: workflowActive,
            detail: workflowActive
                ? "This workflow is active and can be dispatched."
                : state.loopStatus === "retired"
                    ? "This workflow is retired — it can never dispatch."
                    : "A workflow must be active to dispatch. It's a draft right now.",
            fix: workflowActive || state.loopStatus === "retired"
                ? undefined
                : { kind: "toggle", token: "workflow" },
        },
        {
            id: "subscription",
            label: "Trigger enabled",
            ok: subEnabled,
            detail: !state.hasSubscription
                ? "No trigger yet — pick an event in the “When…” card above and save."
                : subEnabled
                    ? "The trigger subscription is enabled and watching for events."
                    : "The trigger subscription exists but is disabled.",
            fix: state.hasSubscription && !subEnabled ? { kind: "toggle", token: "subscription" } : undefined,
        },
        {
            id: "autorun",
            label: "Dispatch runs",
            ok: state.autorunEnabled,
            detail: state.autorunEnabled
                ? "Matching events spawn workflow runs (this spends API tokens)."
                : "Off — a fired trigger queues but no run spawns. Turning this on spawns runs that cost tokens.",
            fix: state.autorunEnabled ? undefined : { kind: "toggle", token: "autorun" },
        },
        {
            id: "drainer",
            label: "Scheduler live",
            ok: state.drainerLive,
            detail: state.drainerLive
                ? "The autopilot tick is running — triggers drain within one tick."
                : "The drainer isn't running. Triggers fire on the autopilot tick; start the in-app scheduler or install the OS daemon.",
            fix: state.drainerLive ? undefined : { kind: "link", href: "/settings/agents/autopilot" },
        },
    ];
    const blocking = gates.filter((g) => !g.ok);
    return { gates, armed: blocking.length === 0, blocking };
}
